import React, { useState } from "react";
import {
  HomeIcon,
  UserGroupIcon,
  CashIcon,
  ChartBarIcon,
  ReceiptTaxIcon,
  DocumentReportIcon,
  CubeIcon,
  ShoppingCartIcon,
  CollectionIcon,
  ClipboardListIcon,
  UserIcon,
  CalendarIcon,
  ChartPieIcon,
  BookOpenIcon,
  ScaleIcon,
  CreditCardIcon,
  TrendingUpIcon,
  SwitchHorizontalIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArchiveIcon,
  ClipboardCheckIcon,
  OfficeBuildingIcon,
  LockClosedIcon,
  CalculatorIcon,
  DocumentAddIcon,
  ClockIcon,
  IdentificationIcon,
} from "@heroicons/react/outline";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BusinessHeader from "../../Business/BusinessHeader";
import { classNames } from "../../../../Utils";

const sections = {
  main: {
    label: "menu.mainMenu",
    items: [
      {
        id: "dashboard",
        text: "menu.dashboard",
        icon: HomeIcon,
        path: "/v2/accounting",
      },
      {
        id: "cash-flow",
        text: "accounting.cashFlow",
        icon: ChartBarIcon,
        path: "/v2/accounting/cashflow",
      },
    ],
  },
  transactions: {
    label: "transactions.label",
    items: [
      {
        id: "transactions",
        text: "accounting.transactions.label",
        icon: ClipboardListIcon,
        path: "/v2/accounting/transactions",
      },
      {
        id: "incomes",
        text: "accounting.incomes.label",
        icon: ShoppingCartIcon,
        path: "/v2/accounting/incomes",
      },
      {
        id: "expenses",
        text: "accounting.expenses.label",
        icon: CashIcon,
        path: "/v2/accounting/expenses",
      },
    ],
  },
  business: {
    label: "businesses.label_one",
    items: [
      {
        id: "coa",
        text: "accounting.coa",
        icon: CollectionIcon,
        path: "/v2/accounting/coa",
        subItems: [
          {
            id: "opening-balance",
            text: "Opening Balance",
            icon: ArchiveIcon,
            path: "/v2/accounting/coa/opening-balance",
          },
        ],
      },
      {
        id: "products",
        text: "products.label",
        icon: CubeIcon,
        path: "/v2/accounting/products",
      },
      {
        id: "product-categories",
        text: "products.categories.label",
        icon: CollectionIcon,
        path: "/v2/accounting/product-categories",
      },
      {
        id: "contacts",
        text: "contacts.label",
        icon: UserGroupIcon,
        path: "/v2/accounting/contacts",
      },
      {
        id: "taxes",
        text: "taxes.label",
        icon: ReceiptTaxIcon,
        path: "/v2/accounting/taxes",
      },
      {
        id: "fixed-assets",
        text: "fixedAssets.label",
        icon: OfficeBuildingIcon,
        path: "/v2/accounting/fixed-assets",
      },
      {
        id: "period",
        text: "accounting.period.label",
        icon: LockClosedIcon,
        path: "/v2/accounting/period",
      },
      {
        id: "employees",
        text: "employees.label",
        icon: UserIcon,
        path: "/v2/accounting/employees",
        subItems: [
          {
            id: "attendance",
            text: "employees.attendances.title",
            icon: ClipboardCheckIcon,
            path: "/v2/accounting/employees/attendance",
          },
        ],
      },
      {
        id: "payroll",
        text: "payroll.label",
        icon: CalculatorIcon,
        path: "/v2/accounting/payroll",
        subItems: [
          {
            id: "payrollOpeningBalance",
            text: "payroll.openingBalance.label",
            icon: DocumentAddIcon,
            path: "/v2/accounting/payroll/opening-balance",
          },
        ],
      },
      {
        id: "calendars",
        text: "employees.calendars.label",
        icon: CalendarIcon,
        path: "/v2/accounting/calendars",
      },
    ],
  },
  reports: {
    label: "accounting.reports.label",
    items: [
      {
        id: "summary",
        text: "reports.summaries.name",
        icon: ChartPieIcon,
        path: "/v2/accounting/reports/summary",
      },
      {
        id: "reports",
        text: "accounting.reports.label",
        icon: DocumentReportIcon,
        path: "/v2/accounting/reports",
        subItems: [
          {
            id: "general-journal",
            text: "reports.generalJournals.name",
            icon: BookOpenIcon,
            path: "/v2/accounting/reports/general-journal",
          },
          {
            id: "balance-sheet",
            text: "reports.balanceSheets.name",
            icon: ScaleIcon,
            path: "/v2/accounting/reports/balance-sheet",
          },
          {
            id: "trial-balance",
            text: "reports.trialBalances.name",
            icon: CreditCardIcon,
            path: "/v2/accounting/reports/trial-balance",
          },
          {
            id: "income-statement",
            text: "reports.incomeStatements.name",
            icon: ChartPieIcon,
            path: "/v2/accounting/reports/income-statement",
          },
          {
            id: "capital-statement",
            text: "reports.capitalStatements.name",
            icon: TrendingUpIcon,
            path: "/v2/accounting/reports/capital-statement",
          },
          {
            id: "cash-flow",
            text: "reports.cashFlow.name",
            icon: SwitchHorizontalIcon,
            path: "/v2/accounting/reports/cash-flow",
          },
          {
            id: "vat-recap",
            text: "reports.vatRecap.name",
            icon: ReceiptTaxIcon,
            path: "/v2/accounting/reports/vat-recap",
          },
          {
            id: "aging",
            text: "reports.aging.name",
            icon: ClockIcon,
            path: "/v2/accounting/reports/aging",
          },
          {
            id: "partner-ledger",
            text: "reports.partnerLedger.name",
            icon: IdentificationIcon,
            path: "/v2/accounting/reports/partner-ledger",
          },
        ],
      },
    ],
  },
};

const MenuItem = ({ icon: Icon, text, isActive, onClick, path, subItems }) => {
  const { t } = useTranslation();
  const location = useLocation();

  const isSubActive = subItems?.some((sub) =>
    location.pathname.startsWith(sub.path),
  );
  const isParentActive = location.pathname === path;
  const [isOpen, setIsOpen] = useState(isParentActive || isSubActive);

  React.useEffect(() => {
    if (isParentActive || isSubActive) {
      setIsOpen(true);
    }
  }, [isParentActive, isSubActive]);

  const toggleOpen = (e) => {
    setIsOpen((prev) => !prev);
  };

  const baseClasses =
    "group border-l-4 px-4 sm:px-6 py-2 flex items-center w-full text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2";
  let activeClasses =
    "bg-teal-50 border-blue-500 text-blue-700 hover:bg-teal-50 hover:text-blue-700";
  let inactiveClasses =
    "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900";

  const isActuallyActive = isActive;

  const classes = `${baseClasses} ${
    isActuallyActive ? activeClasses : inactiveClasses
  }`;

  const renderContent = () => (
    <>
      <Icon
        className={`flex-shrink-0 -ml-1 mr-2 h-6 w-6 ${
          isActuallyActive
            ? "text-blue-500"
            : "text-gray-400 group-hover:text-gray-500"
        }`}
      />
      <span className="truncate flex-1 text-left">{t(text)}</span>
      {subItems && (
        <span
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleOpen(e);
          }}
          className="ml-auto inline-flex items-center justify-center p-1 rounded-md hover:bg-gray-200"
        >
          {isOpen ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-500" />
          )}
        </span>
      )}
    </>
  );

  return (
    <div>
      {path ? (
        <Link to={path} className={classes}>
          {renderContent()}
        </Link>
      ) : (
        <button
          type="button"
          onClick={subItems ? toggleOpen : onClick}
          className={classes}
        >
          {renderContent()}
        </button>
      )}

      {subItems && isOpen && (
        <div className="mt-1 space-y-1 bg-gray-50 py-1">
          {subItems.map((sub) => {
            const isSubItemActive = location.pathname.startsWith(sub.path);
            const subClasses = `group px-4 sm:px-6 py-2 pl-12 sm:pl-14 flex items-center w-full text-sm font-medium ${
              isSubItemActive
                ? "bg-teal-50 text-blue-700 border-l-4 border-blue-500"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-4 border-transparent"
            }`;
            return (
              <Link key={sub.id} to={sub.path} className={subClasses}>
                <sub.icon
                  className={`flex-shrink-0 mr-3 h-5 w-5 ${
                    isSubItemActive
                      ? "text-blue-500"
                      : "text-gray-400 group-hover:text-gray-500"
                  }`}
                />
                <span className="truncate">{t(sub.text)}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AccountingSidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const renderSection = (sectionKey) => {
    const section = sections[sectionKey];
    return (
      <div key={sectionKey} className="mb-4">
        <span className="px-4 sm:px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {t(section.label)}
        </span>
        <nav className="mt-2 space-y-1">
          {section.items.map((menu) => (
            <MenuItem
              key={menu.id}
              {...menu}
              text={t(menu.text)}
              isActive={location.pathname === menu.path}
            />
          ))}
        </nav>
      </div>
    );
  };

  return (
    <aside className="py-6 lg:col-span-3">
      <BusinessHeader onToggleSidebar={toggleSidebar} />
      <div
        className={classNames(isSidebarOpen ? "block" : "hidden", "lg:block")}
      >
        {Object.keys(sections).map(renderSection)}
      </div>
    </aside>
  );
};

export default AccountingSidebar;
